import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import type { Transaction } from '@domain/finance'
import type { Receipt } from '@domain/receipt'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { prepareImage } from '@ui/notas/prepare-image'
import { EscolherNota } from '@ui/notas/EscolherNota'
import { vincularNota } from '@ui/notas/vincular'

/**
 * O bloco de nota fiscal dentro do detalhe de uma transação.
 *
 * ## Anexar daqui já nasce vinculado, e é o caminho bom
 *
 * *"Uma nota fiscal sempre deve estar vinculada a uma transação."* Quando a foto
 * sobe **daqui**, o `transactionId` já vai preenchido — não há conciliação para
 * acertar, não há palpite para conferir. O caminho de subir solto continua
 * existindo porque o sync demora, mas ele é o desvio, não a rota.
 *
 * ## Uma transação pode ter mais de uma nota
 *
 * Compra em duas notas no mesmo caixa acontece — e o teto do breakdown é o valor
 * da transação, então duas notas somam dentro dele sem estourar. A tela lista
 * todas em vez de assumir que existe uma.
 *
 * ## "Selecionar nota existente": a nota que subiu solta, vinculada daqui
 *
 * Nota fotografada antes de a transação chegar do banco sobe solta, e até aqui
 * o único jeito de vinculá-la era abrir a nota e procurar a transação. O botão
 * faz o caminho inverso, com as mesmas sugestões por valor e data
 * (`EscolherNota`), e a mesma regra de categoria (`vincularNota`).
 *
 * **Ele só aparece quando existe nota solta.** Um botão que abre uma lista
 * vazia é uma promessa que a tela não cumpre — e a leitura que decide isso é a
 * mesma que já lista as notas vinculadas, sem custo a mais.
 */
export function NotaDaTransacao({
  transaction,
  onCategorizou,
}: {
  transaction: Transaction
  /** A categoria que a nota vinculada levou para a transação, para o detalhe se corrigir. */
  onCategorizou: (categoryId: string) => void
}) {
  const { receiptRepository, transactionRepository } = useFinanceRepositories()
  const navigate = useNavigate()
  const transactionId = transaction.id

  const [todas, setTodas] = useState<Receipt[]>([])
  const [escolhendo, setEscolhendo] = useState(false)
  const [vinculando, setVinculando] = useState(false)
  const notas = todas.filter((n) => n.transactionId === transactionId)
  const soltas = todas.filter((n) => n.transactionId === null)
  const [enviando, setEnviando] = useState<'preparando' | 'subindo' | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const recarregar = useCallback(() => {
    void receiptRepository
      .list()
      .then((lidas) => {
        setTodas(lidas)
      })
      .catch((e: unknown) => {
        // A nota é um extra do detalhe: falhar em listá-la não pode derrubar a
        // tela que mostra o dinheiro.
        console.error('NotaDaTransacao: a leitura falhou', e)
      })
  }, [receiptRepository])

  useEffect(recarregar, [recarregar])

  async function enviar(file: File) {
    setEnviando('preparando')
    setErro(null)
    try {
      const preparada = await prepareImage(file)
      setEnviando('subindo')
      const id = await receiptRepository.create(preparada.blob, {
        // Já vinculada. Ver o cabeçalho.
        transactionId,
        extracted: null,
      })
      await navigate({ to: '/notas/$id', params: { id } })
    } catch (e: unknown) {
      console.error('NotaDaTransacao: o envio falhou', e)
      setErro('não deu para enviar a foto — tente de novo')
      setEnviando(null)
    }
  }

  return (
    <section aria-label="Nota fiscal" className="mt-6 border-t border-[var(--border)] pt-4">
      <h2 className="mb-2 text-sm font-medium">Nota fiscal</h2>

      {notas.length > 0 && (
        <ul aria-label="Notas desta transação" className="mb-3">
          {notas.map((nota) => (
            <li key={nota.id} className="py-1 text-sm">
              <Link to="/notas/$id" params={{ id: nota.id }} className="alvo justify-start underline">
                {nota.extracted?.merchantName ?? 'Nota fiscal'}
              </Link>
              <span className="ml-2 text-xs text-[var(--text-muted)]">{ROTULO[nota.status]}</span>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label="Foto do cupom desta transação"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file !== undefined) void enviar(file)
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={enviando !== null || vinculando}
          className="rounded-lg border border-[var(--border)] alvo px-3 py-2 text-sm disabled:opacity-50"
          onClick={() => {
            input.current?.click()
          }}
        >
          {enviando === 'preparando'
            ? 'Preparando a foto…'
            : enviando === 'subindo'
              ? 'Enviando…'
              : notas.length === 0
                ? 'Anexar a nota fiscal'
                : 'Anexar outra nota'}
        </button>
        {soltas.length > 0 && (
          <button
            type="button"
            disabled={enviando !== null || vinculando}
            className="rounded-lg border border-[var(--border)] alvo px-3 py-2 text-sm disabled:opacity-50"
            onClick={() => {
              setEscolhendo(true)
            }}
          >
            {vinculando ? 'Vinculando…' : 'Selecionar nota existente'}
          </button>
        )}
      </div>

      {escolhendo && (
        <EscolherNota
          transacao={transaction}
          notas={soltas}
          onFechar={() => {
            setEscolhendo(false)
          }}
          onEscolher={(nota) => {
            setEscolhendo(false)
            setVinculando(true)
            setErro(null)
            vincularNota({ receiptRepository, transactionRepository }, nota, transaction)
              .then((categoria) => {
                if (categoria !== null) onCategorizou(categoria)
                recarregar()
              })
              .catch((e: unknown) => {
                console.error('NotaDaTransacao: o vínculo falhou', e)
                setErro('não deu para vincular a nota — tente de novo')
              })
              .finally(() => {
                setVinculando(false)
              })
          }}
        />
      )}
      {erro !== null && (
        <p role="alert" className="mt-2 text-sm">
          {erro}
        </p>
      )}
    </section>
  )
}

const ROTULO: Record<Receipt['status'], string> = {
  uploaded: 'na fila',
  processing: 'lendo…',
  extracted: 'revisar',
  confirmed: 'confirmada',
  failed: 'falhou',
}
